// ============================================================================
// ADMIN FACILITATORS — Tier 1 view for managing certified facilitator list
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  BadgeCheck, Plus, Trash2, Loader, AlertCircle, X, Search, CheckCircle, UserX, UserCheck,
  Download, ArrowUp, ArrowDown, ChevronRight, ChevronLeft, ArrowLeftRight, UserPlus
} from 'lucide-react';
import {
  getFacilitatorReport,
  listSectors,
  certifyUser,
  revokeUserCertification,
  FacilitatorReportRow,
  FacilitatorTier,
  FacilitatorTrainingStatus,
  Sector,
  TIER_LABELS,
  TRAINING_STATUS_LABELS
} from '../services/studio-bookings';
import {
  listUsers,
  UserProfile,
  reinstateUser,
  OffboardResult
} from '../services/userManagement';
import OffboardUserModal from '../components/admin/OffboardUserModal';
import FacilitatorDetails from '../components/facilitators/FacilitatorDetails';
import InterestedCandidates from '../components/facilitators/InterestedCandidates';
import NominateCandidateModal from '../components/facilitators/NominateCandidateModal';
import { TrainingStatusBadge, SectorChip, formatDate } from '../components/facilitators/FacilitatorParts';
import { toCsv, downloadCsv } from '../utils/csv';
import { useAuth } from '../contexts/AuthContext';

function TierBadge({ tier }: { tier: FacilitatorTier }) {
  const cls = tier === 'tier1'
    ? 'bg-purple-50 text-purple-700 border border-purple-200'
    : 'bg-blue-50 text-blue-700 border border-blue-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {TIER_LABELS[tier]}
    </span>
  );
}

type FacilitatorSummary = FacilitatorReportRow;

type SortKey = 'name' | 'bookings' | 'next' | 'training';
type ActiveFilter = 'active' | 'offboarded' | 'all';

const SORT_VALUE: Record<SortKey, (f: FacilitatorReportRow) => string | number> = {
  name: f => f.displayName.toLowerCase(),
  bookings: f => f.bookings.leadAllTime + f.bookings.supportAllTime,
  next: f => f.nextBooking?.startDate ?? '',
  training: f => f.training.nextAssessmentDue ?? ''
};

/** The one date that matters most for the row's current training stage. */
function trainingDetail(t: FacilitatorReportRow['training']): string | null {
  if (t.nextAssessmentDue) return `Reassess by ${formatDate(t.nextAssessmentDue)}`;
  if (t.assessedAt) return `Failed ${formatDate(t.assessedAt)}`;
  if (t.inPersonTrainedAt) return `Trained ${formatDate(t.inPersonTrainedAt)}`;
  if (t.walkthroughCompletedAt) return `Walkthrough ${formatDate(t.walkthroughCompletedAt)}`;
  return null;
}

const csvDate = (v?: string) => (v ? v.slice(0, 10) : '');

function exportFacilitatorsCsv(rows: FacilitatorReportRow[]) {
  const headers = [
    'Name', 'Email', 'Tier', 'Status', 'Primary sector', 'Secondary sectors',
    'Lead bookings (all time)', 'Lead bookings (last 12 months)',
    'Support bookings (all time)', 'Support bookings (last 12 months)',
    'Next booking date', 'Next booking customer', 'Next booking role',
    'Training status', 'Walkthrough completed', 'Last in-person training',
    'Last assessed', 'Last assessment outcome', 'Reassessment due'
  ];
  const sectorName = (s: { name: string; isActive: boolean }) => (s.isActive ? s.name : `${s.name} (inactive)`);
  const data = rows.map(f => [
    f.displayName,
    f.email,
    TIER_LABELS[f.tier] + (f.isSystemAdmin ? ' (admin)' : ''),
    f.isOffboarded ? 'Offboarded' : 'Active',
    f.primarySector ? sectorName(f.primarySector) : '',
    f.secondarySectors.map(sectorName).join('; '),
    f.bookings.leadAllTime,
    f.bookings.leadLast12m,
    f.bookings.supportAllTime,
    f.bookings.supportLast12m,
    csvDate(f.nextBooking?.startDate),
    f.nextBooking?.customer ?? '',
    f.nextBooking?.role ?? '',
    TRAINING_STATUS_LABELS[f.training.status],
    csvDate(f.training.walkthroughCompletedAt),
    csvDate(f.training.inPersonTrainedAt),
    csvDate(f.training.assessedAt),
    f.training.assessmentOutcome ?? '',
    csvDate(f.training.nextAssessmentDue)
  ]);
  downloadCsv(toCsv(headers, data), `facilitators-${new Date().toISOString().slice(0, 10)}.csv`);
}

function SortHeader({
  label, sortKey, sort, onSort, className = ''
}: {
  label: string;
  sortKey?: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const cls = `px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap ${className}`;
  if (!sortKey) return <th className={cls}>{label}</th>;
  const active = sort.key === sortKey;
  return (
    <th className={cls}>
      <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 uppercase ${active ? 'text-navy' : 'hover:text-slate-600'}`}>
        {label}
        {active && (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </button>
    </th>
  );
}

function BookingTotal({ b }: { b: FacilitatorReportRow['bookings'] }) {
  const all = b.leadAllTime + b.supportAllTime;
  const recent = b.leadLast12m + b.supportLast12m;
  return (
    <span
      className="text-sm text-navy font-semibold whitespace-nowrap cursor-help"
      title={`Lead: ${b.leadAllTime} (${b.leadLast12m} in last 12 months)\nSupport: ${b.supportAllTime} (${b.supportLast12m} in last 12 months)`}
    >
      {all} <span className="font-normal text-slate-400">({recent})</span>
    </span>
  );
}

function SectorSummary({ row }: { row: FacilitatorReportRow }) {
  const { primarySector, secondarySectors } = row;
  if (!primarySector && secondarySectors.length === 0) return <span className="text-sm text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {primarySector && <SectorChip sector={primarySector} primary />}
      {secondarySectors.length > 0 && (
        <span
          className="text-xs font-semibold text-slate-500 cursor-help"
          title={secondarySectors.map(s => s.isActive ? s.name : `${s.name} (inactive)`).join('\n')}
        >
          +{secondarySectors.length}
        </span>
      )}
    </span>
  );
}

const actionBtn = 'flex items-center gap-1.5 rounded-xl h-9 px-3.5 border border-slate-200 bg-white text-xs font-bold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

const PAGE_SIZES = [10, 25, 50];

/** Page numbers with ellipses: 1 … 4 5 6 … 12 */
function pageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1].filter(p => p >= 1 && p <= total));
  const sorted = [...pages].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? ['…' as const, p] : [p]));
}

function Pagination({
  page, pageSize, total, onPage, onPageSize
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const navBtn = 'flex items-center justify-center min-w-8 h-8 px-2 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[#e2e8f0] bg-slate-50/50">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={e => onPageSize(Number(e.target.value))}
          className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-xs text-navy bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        >
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="ml-2">{from}–{to} of {total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} className={`${navBtn} text-slate-500 hover:bg-white`} aria-label="Previous page">
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pageList(page, pages).map((p, i) => p === '…' ? (
          <span key={`gap-${i}`} className="px-1 text-xs text-slate-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPage(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`${navBtn} ${p === page ? 'bg-navy text-white' : 'text-slate-600 hover:bg-white'}`}
          >
            {p}
          </button>
        ))}
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} className={`${navBtn} text-slate-500 hover:bg-white`} aria-label="Next page">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const selectCls = 'border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none';

export default function AdminFacilitators() {
  const { user: currentUser, can } = useAuth();
  const canOffboard = can('Admin::ManageUsers');
  const canEditTraining = can('Facilitation::ManageTraining');

  const [facilitators, setFacilitators] = useState<FacilitatorSummary[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<FacilitatorTier | ''>('');
  const [primaryFilter, setPrimaryFilter] = useState('');
  const [anySectorFilter, setAnySectorFilter] = useState('');
  const [trainingFilter, setTrainingFilter] = useState<FacilitatorTrainingStatus | ''>('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedTier, setSelectedTier] = useState<FacilitatorTier>('tier2');
  const [certifying, setCertifying] = useState(false);
  const [certifyError, setCertifyError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [changingTier, setChangingTier] = useState<string | null>(null);
  const [reinstating, setReinstating] = useState<string | null>(null);

  const [offboardTarget, setOffboardTarget] = useState<FacilitatorSummary | null>(null);

  const [view, setView] = useState<'certified' | 'interested'>('certified');
  const [showNominateModal, setShowNominateModal] = useState(false);
  const [candidatesReloadKey, setCandidatesReloadKey] = useState(0);

  function load(showSpinner = true) {
    if (showSpinner) setLoading(true);
    getFacilitatorReport()
      .then(r => {
        setFacilitators(r.facilitators);
        setCanManage(r.canManage);
      })
      .catch(() => setError('Failed to load facilitators'))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    listSectors().then(setSectors).catch(() => {});
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  async function openCertifyModal() {
    setShowCertifyModal(true);
    setCertifyError(null);
    setSelectedUser(null);
    setUserSearch('');
    try {
      const result = await listUsers({ status: 'active' });
      const certifiedIds = new Set(facilitators.map(f => f.userId));
      setAllUsers(result.users.filter(u => !certifiedIds.has(u.userId)));
    } catch {
      setCertifyError('Failed to load users');
    }
  }

  async function handleCertify() {
    if (!selectedUser) return;
    setCertifying(true);
    setCertifyError(null);
    try {
      await certifyUser(selectedUser.userId, selectedTier);
      setShowCertifyModal(false);
      setSuccess(`${selectedUser.displayName} certified as ${TIER_LABELS[selectedTier]}`);
      load();
    } catch (err: any) {
      setCertifyError(err.response?.data?.error || 'Failed to certify user');
    } finally {
      setCertifying(false);
    }
  }

  async function handleChangeTier(facilitator: FacilitatorSummary, tier: FacilitatorTier) {
    if (!window.confirm(`Move ${facilitator.displayName} from ${TIER_LABELS[facilitator.tier]} to ${TIER_LABELS[tier]}? ${
      tier === 'tier1' ? 'Tier 1 can approve bookings and manage the facilitator list.' : 'They will lose Tier 1 permissions.'}`)) return;
    setChangingTier(facilitator.userId);
    try {
      await certifyUser(facilitator.userId, tier);
      setSuccess(`${facilitator.displayName} moved to ${TIER_LABELS[tier]}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change tier');
    } finally {
      setChangingTier(null);
    }
  }

  async function handleRevoke(facilitator: FacilitatorSummary) {
    if (!window.confirm(`Remove ${facilitator.tier === 'tier1' ? 'Tier 1' : 'Tier 2'} certification from ${facilitator.displayName}?`)) return;
    setRevoking(facilitator.userId);
    try {
      await revokeUserCertification(facilitator.userId);
      setSuccess(`${facilitator.displayName}'s certification removed`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke certification');
    } finally {
      setRevoking(null);
    }
  }

  function handleOffboarded(result: OffboardResult) {
    if (offboardTarget && !result.partial) {
      setSuccess(`${offboardTarget.displayName} has been offboarded`);
    }
    load();
  }

  async function handleReinstate(facilitator: FacilitatorSummary) {
    if (!window.confirm(`Reinstate ${facilitator.displayName}? Their account will be reactivated. They'll need to create new project MCP tokens themselves.`)) return;
    setReinstating(facilitator.userId);
    try {
      await reinstateUser(facilitator.userId);
      setSuccess(`${facilitator.displayName} has been reinstated`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reinstate facilitator');
    } finally {
      setReinstating(null);
    }
  }

  const filteredUsers = allUsers.filter(u =>
    userSearch === '' ||
    u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Filter options include inactive sectors still carried as tags.
  const sectorOptions = useMemo(() => {
    const byId = new Map(sectors.map(s => [s.sectorId, s.name]));
    facilitators.forEach(f => [f.primarySector, ...f.secondarySectors].forEach(s => {
      if (s && !byId.has(s.sectorId)) byId.set(s.sectorId, `${s.name} (inactive)`);
    }));
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [sectors, facilitators]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = facilitators.filter(f =>
      (!q || f.displayName.toLowerCase().includes(q) || f.email.toLowerCase().includes(q)) &&
      (!tierFilter || f.tier === tierFilter) &&
      (!primaryFilter || f.primarySector?.sectorId === primaryFilter) &&
      (!anySectorFilter || f.primarySector?.sectorId === anySectorFilter || f.secondarySectors.some(s => s.sectorId === anySectorFilter)) &&
      (!trainingFilter || f.training.status === trainingFilter) &&
      (activeFilter === 'all' || (activeFilter === 'offboarded') === !!f.isOffboarded)
    );
    const value = SORT_VALUE[sort.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const va = value(a), vb = value(b);
      // Blank dates sort last regardless of direction.
      if (va === '' && vb !== '') return 1;
      if (vb === '' && va !== '') return -1;
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir || a.displayName.localeCompare(b.displayName);
    });
  }, [facilitators, search, tierFilter, primaryFilter, anySectorFilter, trainingFilter, activeFilter, sort]);

  // Back to page 1 whenever the filtered/sorted set changes; clamp if rows disappear.
  useEffect(() => setPage(1), [search, tierFilter, primaryFilter, anySectorFilter, trainingFilter, activeFilter, sort, pageSize]);
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const filtersActive = !!(search || tierFilter || primaryFilter || anySectorFilter || trainingFilter || activeFilter !== 'active');

  function handleSort(key: SortKey) {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'bookings' ? 'desc' : 'asc' });
  }

  function clearFilters() {
    setSearch('');
    setTierFilter('');
    setPrimaryFilter('');
    setAnySectorFilter('');
    setTrainingFilter('');
    setActiveFilter('active');
  }

  function toggleRow(userId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <BadgeCheck size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Certified Facilitators</h1>
            <p className="text-slate-500 text-sm font-medium">Sectors, bookings delivered and training status for Tier 1 and Tier 2 facilitators</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowNominateModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 border border-[#e2e8f0] bg-white hover:bg-slate-50 transition-colors text-navy text-sm font-bold"
          >
            <UserPlus className="w-4 h-4" /> Nominate
          </button>
        {canManage && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => exportFacilitatorsCsv(visible)}
              disabled={visible.length === 0}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 border border-[#e2e8f0] bg-white hover:bg-slate-50 transition-colors text-navy text-sm font-bold disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={openCertifyModal}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <Plus className="w-4 h-4" /> Certify user
            </button>
          </div>
        )}
        </div>
      </header>

      {canManage && (
        <div className="flex gap-1 border-b border-slate-200">
          {([['certified', 'Certified'], ['interested', 'Interested']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${
                view === v ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-navy'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700 font-medium">{success}</p>
        </div>
      )}

      {canManage && view === 'interested' ? (
        <InterestedCandidates
          reloadKey={candidatesReloadKey}
          onChanged={() => load(false)}
          onMessage={setSuccess}
          onError={setError}
        />
      ) : loading ? (
        <div className="flex justify-center h-40 items-center">
          <Loader className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl pl-9 pr-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <select value={tierFilter} onChange={e => setTierFilter(e.target.value as FacilitatorTier | '')} className={selectCls}>
              <option value="">All tiers</option>
              <option value="tier1">{TIER_LABELS.tier1}</option>
              <option value="tier2">{TIER_LABELS.tier2}</option>
            </select>
            <select value={primaryFilter} onChange={e => setPrimaryFilter(e.target.value)} className={selectCls}>
              <option value="">Any primary sector</option>
              {sectorOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select value={anySectorFilter} onChange={e => setAnySectorFilter(e.target.value)} className={selectCls}>
              <option value="">Supports any sector</option>
              {sectorOptions.map(([id, name]) => <option key={id} value={id}>Supports {name}</option>)}
            </select>
            <select value={trainingFilter} onChange={e => setTrainingFilter(e.target.value as FacilitatorTrainingStatus | '')} className={selectCls}>
              <option value="">All training statuses</option>
              {(Object.keys(TRAINING_STATUS_LABELS) as FacilitatorTrainingStatus[]).map(s => (
                <option key={s} value={s}>{TRAINING_STATUS_LABELS[s]}</option>
              ))}
            </select>
            <select value={activeFilter} onChange={e => setActiveFilter(e.target.value as ActiveFilter)} className={selectCls}>
              <option value="active">Active</option>
              <option value="offboarded">Offboarded</option>
              <option value="all">Active and offboarded</option>
            </select>
            {filtersActive && (
              <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-navy underline-offset-2 hover:underline">
                Clear filters
              </button>
            )}
            </div>

            {visible.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                {facilitators.length === 0 ? 'No facilitators certified yet' : 'No facilitators match these filters'}
              </div>
            ) : (
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="w-10" />
                    <SortHeader label="Name" sortKey="name" sort={sort} onSort={handleSort} className="w-[28%]" />
                    <SortHeader label="Tier" sort={sort} onSort={handleSort} className="w-24" />
                    <SortHeader label="Sectors" sort={sort} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortHeader label="Bookings" sortKey="bookings" sort={sort} onSort={handleSort} className="w-28" />
                    <SortHeader label="Next booking" sortKey="next" sort={sort} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortHeader label="Training" sortKey="training" sort={sort} onSort={handleSort} className="w-48" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(f => {
                    const open = expanded.has(f.userId);
                    const detail = trainingDetail(f.training);
                    return (
                      <React.Fragment key={f.userId}>
                        <tr
                          onClick={() => toggleRow(f.userId)}
                          aria-expanded={open}
                          className={`cursor-pointer border-b border-[#e2e8f0] transition-colors ${open ? 'bg-slate-50/60' : 'hover:bg-slate-50/50'} ${f.isOffboarded ? 'opacity-60' : ''}`}
                        >
                          <td className="pl-4 py-3">
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                          </td>
                          <td className="px-3 py-3">
                            <p className="text-sm font-semibold text-navy truncate">
                              {f.displayName}
                              {f.isSystemAdmin && (
                                <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 align-middle">
                                  Admin
                                </span>
                              )}
                              {f.isOffboarded && (
                                <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 align-middle">
                                  Offboarded
                                </span>
                              )}
                              {f.tier === 'tier1' && !f.isSystemAdmin && !f.isOffboarded && !f.homeRoomIds?.length && (
                                <span
                                  className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 align-middle"
                                  title="Not counted in calendar facilitator availability until a home studio is set"
                                >
                                  No home studio
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{f.email}</p>
                          </td>
                          <td className="px-3 py-3"><TierBadge tier={f.tier} /></td>
                          <td className="px-3 py-3 hidden lg:table-cell"><SectorSummary row={f} /></td>
                          <td className="px-3 py-3"><BookingTotal b={f.bookings} /></td>
                          <td className="px-3 py-3 hidden lg:table-cell">
                            {f.nextBooking ? (
                              <>
                                <p className="text-sm text-navy whitespace-nowrap">{formatDate(f.nextBooking.startDate)}</p>
                                <p className="text-xs text-slate-400 truncate" title={f.nextBooking.customer}>
                                  {f.nextBooking.customer}{f.nextBooking.role === 'support' ? ' · support' : ''}
                                </p>
                              </>
                            ) : <span className="text-sm text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-3">
                            <TrainingStatusBadge status={f.training.status} />
                            {detail && (
                              <p className={`text-xs mt-1 whitespace-nowrap ${
                                f.training.status === 'overdue' || f.training.status === 'assessment_failed' ? 'text-red-600 font-semibold'
                                  : f.training.status === 'reassessment_due' ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                                {detail}
                              </p>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr className="border-b border-[#e2e8f0]">
                            <td colSpan={7} className="p-0">
                              <FacilitatorDetails
                                row={f}
                                canViewHistory={canManage || f.userId === currentUser?.userId}
                                canEditTraining={canEditTraining}
                                onTrainingChanged={() => load(false)}
                                actions={canManage && !f.isSystemAdmin ? (
                                  <>
                                    <button
                                      onClick={() => handleChangeTier(f, f.tier === 'tier1' ? 'tier2' : 'tier1')}
                                      disabled={changingTier === f.userId}
                                      className={`${actionBtn} text-navy hover:bg-slate-50`}
                                    >
                                      {changingTier === f.userId ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ArrowLeftRight className="w-3.5 h-3.5" />}
                                      Change to {TIER_LABELS[f.tier === 'tier1' ? 'tier2' : 'tier1']}
                                    </button>
                                    {canOffboard && (f.isOffboarded ? (
                                      <button
                                        onClick={() => handleReinstate(f)}
                                        disabled={reinstating === f.userId}
                                        className={`${actionBtn} text-emerald-700 hover:bg-emerald-50`}
                                      >
                                        {reinstating === f.userId ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                                        Reinstate
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => setOffboardTarget(f)}
                                        disabled={f.userId === currentUser?.userId}
                                        title={f.userId === currentUser?.userId ? "You can't offboard your own account" : undefined}
                                        className={`${actionBtn} text-amber-700 hover:bg-amber-50`}
                                      >
                                        <UserX className="w-3.5 h-3.5" />
                                        Offboard
                                      </button>
                                    ))}
                                    <button
                                      onClick={() => handleRevoke(f)}
                                      disabled={revoking === f.userId}
                                      className={`${actionBtn} text-red-600 hover:bg-red-50 ml-auto`}
                                    >
                                      {revoking === f.userId ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                      Remove certification
                                    </button>
                                  </>
                                ) : canManage && f.isSystemAdmin ? (
                                  <p className="text-xs text-slate-400">System admins are managed via User Management.</p>
                                ) : undefined}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {visible.length > PAGE_SIZES[0] && (
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={visible.length}
                onPage={setPage}
                onPageSize={setPageSize}
              />
            )}
          </div>

          <p className="text-xs text-slate-400">
            Showing {visible.length} of {facilitators.length}. Bookings are approved bookings already delivered (lead and support), all time with the last 12 months in brackets. Hover for the split.
          </p>
        </div>
      )}

      </div>
      {showNominateModal && (
        <NominateCandidateModal
          onClose={() => setShowNominateModal(false)}
          onNominated={name => {
            setShowNominateModal(false);
            setSuccess(`${name} added to the interested list`);
            setCandidatesReloadKey(k => k + 1);
          }}
        />
      )}
      {showCertifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-[#e2e8f0]">
            <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
              <h2 className="font-bold text-navy text-lg">Certify a user</h2>
              <button onClick={() => setShowCertifyModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {certifyError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{certifyError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Search users</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email…"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl pl-9 pr-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                </div>
                <div className="mt-2 max-h-44 overflow-y-auto border border-[#e2e8f0] rounded-xl divide-y divide-[#e2e8f0]">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No users found</p>
                  ) : filteredUsers.slice(0, 20).map(u => (
                    <button
                      key={u.userId}
                      type="button"
                      onClick={() => setSelectedUser(u)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        selectedUser?.userId === u.userId
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-slate-50 text-navy'
                      }`}
                    >
                      <span className="font-semibold">{u.displayName}</span>
                      <span className="ml-2 text-slate-400 text-xs">{u.email}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Certification tier</label>
                <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                  {(['tier2', 'tier1'] as FacilitatorTier[]).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelectedTier(t)}
                      className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                        selectedTier === t ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {TIER_LABELS[t]}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {selectedTier === 'tier1'
                    ? 'Tier 1 — Core team. Can approve bookings and manage the facilitator list.'
                    : 'Tier 2 — Certified facilitator. Can be selected to host sessions.'}
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowCertifyModal(false)}
                  className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCertify}
                  disabled={!selectedUser || certifying}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-all shadow-md shadow-primary/20"
                >
                  {certifying ? 'Certifying…' : 'Certify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {offboardTarget && (
        <OffboardUserModal
          userId={offboardTarget.userId}
          displayName={offboardTarget.displayName}
          isFacilitator
          onClose={() => setOffboardTarget(null)}
          onOffboarded={handleOffboarded}
        />
      )}
    </div>
  );
}
